import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import {
  Plus,
  Trash2,
  ChevronDown,
  Save
} from 'lucide-react';
import templateService from '../services/templateService';

// Custom Autocomplete Combobox for MongoDB paths
const PathCombobox = ({ value, onChange, options, placeholder }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const filteredOptions = options.filter(opt =>
    opt.toLowerCase().includes(inputValue.toLowerCase())
  );

  return (
    <div className="relative">
      <Input
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          onChange(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        placeholder={placeholder}
      />
      {isOpen && filteredOptions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredOptions.slice(0, 10).map((opt, idx) => (
            <div
              key={idx}
              className="px-3 py-2 hover:bg-muted cursor-pointer text-sm"
              onMouseDown={() => {
                setInputValue(opt);
                onChange(opt);
                setIsOpen(false);
              }}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ManualTemplateMapper = ({ open, onClose, template, onSave }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [metadata, setMetadata] = useState({
    entity_type: template?.entity_type || 'quotation',
    header_mappings: [],
    table_config: {
      header_row: 7,
      start_row: 8,
      prototype_row: 8,
      columns: [],
      merged_cells: []
    },
    summary_formulas: []
  });

  const safeMetadata = {
    ...metadata,
    header_mappings: metadata.header_mappings || [],
    table_config: {
      ...metadata.table_config,
      columns: metadata.table_config?.columns || [],
      merged_cells: metadata.table_config?.merged_cells || []
    },
    summary_formulas: metadata.summary_formulas || []
  };
  const [validation, setValidation] = useState({ valid: true, warnings: [] });

  const steps = [
    'Basic Configuration',
    'Header Mappings',
    'Table Column Mappings',
    'Formulas & Advanced',
    'Preview & Save'
  ];

  const formatters = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'currency', label: 'Currency (¥)' },
    { value: 'date_chinese', label: 'Chinese Date (X月X号)' },
    { value: 'date_iso', label: 'ISO Date (2025-01-15)' },
    { value: 'formula', label: 'Formula' }
  ];

  const mongodbPathSuggestions = [
    'quotation_number',
    'quotation_id',
    'client_name',
    'quotation_date',
    'status',
    'total_amount',
    'valid_until',
    'contact_person',
    'phone',
    'address',
    'remarks',
    'products.name',
    'products.specifications',
    'products.product_id',
    'products.client_ref_number',
    'products.part_number',
    'products.unit',
    'products.quantity',
    'products.unit_price',
    'products.price',
    'products.total_price',
    'products.remarks',
    'products.product_id|products.client_ref_number|products.part_number',
    'products.unit_price|products.price',
    'products.specifications|products.specification',
    '_sequence',
    '_row'
  ];


  useEffect(() => {
    if (template && open) {
      if (template.metadata && template.metadata_type === 'manual') {
        let loadedMetadata = template.metadata;

        if (loadedMetadata.cellMappings && !loadedMetadata.header_mappings) {
          loadedMetadata = {
            entity_type: loadedMetadata.entity_type,
            header_mappings: loadedMetadata.cellMappings || [],
            table_config: loadedMetadata.tableConfigs?.[0] ? {
              header_row: loadedMetadata.tableConfigs[0].headerRow || 7,
              start_row: loadedMetadata.tableConfigs[0].dataStartRow || 8,
              prototype_row: loadedMetadata.tableConfigs[0].dataStartRow || 8,
              columns: loadedMetadata.tableConfigs[0].columns || [],
              merged_cells: []
            } : {
              header_row: 7,
              start_row: 8,
              prototype_row: 8,
              columns: [],
              merged_cells: []
            },
            summary_formulas: []
          };
        }

        setMetadata(loadedMetadata);
      } else {
        setMetadata(prev => ({
          ...prev,
          entity_type: template.entity_type || 'quotation'
        }));
      }
    }
  }, [template, open]);

  const validateMetadata = useCallback(async () => {
    try {
      const result = await templateService.validateManualMetadata(template._id, metadata);
      setValidation(result);
    } catch (error) {
      console.error('Failed to validate metadata:', error);
      setValidation({ valid: false, warnings: ['Failed to validate metadata'] });
    }
  }, [template, metadata]);

  useEffect(() => {
    const headerMappings = metadata.header_mappings || [];
    const columns = metadata.table_config?.columns || [];
    if (headerMappings.length > 0 || columns.length > 0) {
      validateMetadata();
    }
  }, [metadata, validateMetadata]);

  const handleNext = () => {
    setActiveStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  const addHeaderMapping = () => {
    setMetadata(prev => ({
      ...prev,
      header_mappings: [
        ...prev.header_mappings,
        {
          cell: '',
          mongodb_path: '',
          formatter: 'text',
          prefix: ''
        }
      ]
    }));
  };

  const updateHeaderMapping = (index, field, value) => {
    setMetadata(prev => ({
      ...prev,
      header_mappings: prev.header_mappings.map((mapping, i) =>
        i === index ? { ...mapping, [field]: value } : mapping
      )
    }));
  };

  const removeHeaderMapping = (index) => {
    setMetadata(prev => ({
      ...prev,
      header_mappings: prev.header_mappings.filter((_, i) => i !== index)
    }));
  };

  const addTableColumn = () => {
    setMetadata(prev => ({
      ...prev,
      table_config: {
        ...prev.table_config,
        columns: [
          ...prev.table_config.columns,
          {
            column: '',
            mongodb_path: '',
            formatter: 'text'
          }
        ]
      }
    }));
  };

  const updateTableColumn = (index, field, value) => {
    setMetadata(prev => ({
      ...prev,
      table_config: {
        ...prev.table_config,
        columns: prev.table_config.columns.map((col, i) =>
          i === index ? { ...col, [field]: value } : col
        )
      }
    }));
  };

  const removeTableColumn = (index) => {
    setMetadata(prev => ({
      ...prev,
      table_config: {
        ...prev.table_config,
        columns: prev.table_config.columns.filter((_, i) => i !== index)
      }
    }));
  };

  const addSummaryFormula = () => {
    setMetadata(prev => ({
      ...prev,
      summary_formulas: [
        ...prev.summary_formulas,
        {
          cell: '',
          formula: '',
          formatter: 'formula'
        }
      ]
    }));
  };

  const handleSave = async () => {
    try {
      const result = await templateService.updateManualMetadata(template._id, metadata);
      onSave(result.metadata || result);
      onClose();
    } catch (error) {
      console.error('Failed to save metadata:', error);
    }
  };

  const generateSampleData = () => {
    return {
      quotation_number: 'QT2025001',
      client_name: 'Sample Company Ltd',
      quotation_date: new Date(),
      status: 'Active',
      total_amount: 15000,
      products: [
        {
          product_id: 'PROD001',
          name: 'Sample Product A',
          specifications: 'Size: 10x10cm',
          unit: 'pcs',
          quantity: 100,
          unit_price: 50,
          total_price: 5000
        },
        {
          product_id: 'PROD002',
          name: 'Sample Product B',
          specifications: 'Size: 20x20cm',
          unit: 'pcs',
          quantity: 200,
          unit_price: 25,
          total_price: 5000
        }
      ]
    };
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-4">Basic Template Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Entity Type</Label>
                <Select
                  value={metadata.entity_type}
                  onValueChange={(value) => setMetadata(prev => ({ ...prev, entity_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quotation">Quotation</SelectItem>
                    <SelectItem value="sales">Sales Order</SelectItem>
                    <SelectItem value="purchase">Purchase Order</SelectItem>
                    <SelectItem value="warehouse_inbound">Warehouse Inbound</SelectItem>
                    <SelectItem value="warehouse_outbound">Warehouse Outbound</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Header Row</Label>
                <Input
                  type="number"
                  value={safeMetadata.table_config.header_row}
                  onChange={(e) => setMetadata(prev => ({
                    ...prev,
                    table_config: { ...prev.table_config, header_row: parseInt(e.target.value) }
                  }))}
                />
                <p className="text-xs text-muted-foreground">Row number where table headers are located</p>
              </div>
              <div className="space-y-2">
                <Label>Data Start Row</Label>
                <Input
                  type="number"
                  value={safeMetadata.table_config.start_row}
                  onChange={(e) => setMetadata(prev => ({
                    ...prev,
                    table_config: { ...prev.table_config, start_row: parseInt(e.target.value) }
                  }))}
                />
                <p className="text-xs text-muted-foreground">Row number where data starts</p>
              </div>
              <div className="space-y-2">
                <Label>Prototype Row</Label>
                <Input
                  type="number"
                  value={safeMetadata.table_config.prototype_row}
                  onChange={(e) => setMetadata(prev => ({
                    ...prev,
                    table_config: { ...prev.table_config, prototype_row: parseInt(e.target.value) }
                  }))}
                />
                <p className="text-xs text-muted-foreground">Row to copy styles from</p>
              </div>
            </div>

            <Alert className="mt-4 bg-primary/10 border-primary/20">
              <AlertDescription>
                <strong>Manual Mapping Benefits:</strong>
                <br />• Full control over MongoDB field to Excel column mapping
                <br />• Support for fallback fields (field1|field2|field3)
                <br />• Dynamic formulas with row placeholders
                <br />• Merged cell handling
              </AlertDescription>
            </Alert>
          </div>
        );

      case 1:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Header Field Mappings</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Map Excel cells to MongoDB fields for header information.
            </p>

            <div className="space-y-4">
              {safeMetadata.header_mappings.map((mapping, index) => (
                <Card key={index}>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                      <div className="md:col-span-2 space-y-1">
                        <Label className="text-xs">Excel Cell</Label>
                        <Input
                          value={mapping.cell || ''}
                          onChange={(e) => updateHeaderMapping(index, 'cell', e.target.value)}
                          placeholder="B2"
                        />
                      </div>
                      <div className="md:col-span-4 space-y-1">
                        <Label className="text-xs">MongoDB Path</Label>
                        <PathCombobox
                          options={mongodbPathSuggestions}
                          value={mapping.mongodb_path}
                          onChange={(value) => updateHeaderMapping(index, 'mongodb_path', value)}
                          placeholder="e.g., quotation_number"
                        />
                      </div>
                      <div className="md:col-span-2 space-y-1">
                        <Label className="text-xs">Formatter</Label>
                        <Select
                          value={mapping.formatter}
                          onValueChange={(value) => updateHeaderMapping(index, 'formatter', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {formatters.map(fmt => (
                              <SelectItem key={fmt.value} value={fmt.value}>{fmt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-3 space-y-1">
                        <Label className="text-xs">Prefix</Label>
                        <Input
                          value={mapping.prefix || ''}
                          onChange={(e) => updateHeaderMapping(index, 'prefix', e.target.value)}
                          placeholder="QT-, Order: "
                        />
                      </div>
                      <div className="md:col-span-1 flex items-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() => removeHeaderMapping(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Button
              variant="outline"
              onClick={addHeaderMapping}
              className="w-full mt-4"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Header Mapping
            </Button>
          </div>
        );

      case 2:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Table Column Mappings</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Map table columns to MongoDB product fields. Supports fallback paths with | separator.
            </p>

            <div className="space-y-4">
              {safeMetadata.table_config.columns.map((column, index) => (
                <Card key={index}>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                      <div className="md:col-span-1 space-y-1">
                        <Label className="text-xs">Column</Label>
                        <Input
                          value={column.column}
                          onChange={(e) => updateTableColumn(index, 'column', e.target.value)}
                          placeholder="A"
                        />
                      </div>
                      <div className="md:col-span-5 space-y-1">
                        <Label className="text-xs">MongoDB Path</Label>
                        <PathCombobox
                          options={mongodbPathSuggestions}
                          value={column.mongodb_path}
                          onChange={(value) => updateTableColumn(index, 'mongodb_path', value)}
                          placeholder="e.g., products.name"
                        />
                      </div>
                      <div className="md:col-span-2 space-y-1">
                        <Label className="text-xs">Formatter</Label>
                        <Select
                          value={column.formatter}
                          onValueChange={(value) => updateTableColumn(index, 'formatter', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {formatters.map(fmt => (
                              <SelectItem key={fmt.value} value={fmt.value}>{fmt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-3 space-y-1">
                        {column.formula ? (
                          <>
                            <Label className="text-xs">Formula</Label>
                            <Input
                              value={column.formula}
                              onChange={(e) => updateTableColumn(index, 'formula', e.target.value)}
                              placeholder="=F{row}*G{row}"
                            />
                          </>
                        ) : (
                          <>
                            <Label className="text-xs"> </Label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => updateTableColumn(index, 'formula', '=F{row}*G{row}')}
                            >
                              Add Formula
                            </Button>
                          </>
                        )}
                      </div>
                      <div className="md:col-span-1 flex items-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() => removeTableColumn(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Button
              variant="outline"
              onClick={addTableColumn}
              className="w-full mt-4"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Column Mapping
            </Button>

            <Alert className="mt-4 bg-primary/10 border-primary/20">
              <AlertDescription>
                <strong>MongoDB Path Examples:</strong>
                <br />• <code>products.name</code> - Direct field access
                <br />• <code>products.product_id|products.client_ref_number|products.part_number</code> - Fallback fields
                <br />• <code>_sequence</code> - Auto-increment number (1, 2, 3...)
                <br />• <code>products.unit_price|products.price</code> - Price with fallback
              </AlertDescription>
            </Alert>

            <Card className="mt-4">
              <CardContent className="p-4">
                <h4 className="font-medium mb-2">Common MongoDB Paths Reference</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium mb-1">Header Fields:</p>
                    <p className="text-xs font-mono text-muted-foreground">
                      quotation_number<br />
                      quotation_id<br />
                      client_name<br />
                      quotation_date<br />
                      status<br />
                      total_amount
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">Product Fields:</p>
                    <p className="text-xs font-mono text-muted-foreground">
                      products.name<br />
                      products.specifications<br />
                      products.product_id<br />
                      products.unit<br />
                      products.quantity<br />
                      products.unit_price
                    </p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground italic mt-2">
                  Tip: Use | to separate fallback fields, e.g., products.product_id|products.client_ref_number
                </p>
              </CardContent>
            </Card>
          </div>
        );

      case 3:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-4">Summary Formulas & Advanced Settings</h3>

            <Collapsible defaultOpen>
              <CollapsibleTrigger className="flex items-center gap-2 w-full py-2 text-left font-medium">
                <ChevronDown className="h-4 w-4" />
                Summary Formulas
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4">
                {safeMetadata.summary_formulas.map((formula, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 mb-2 items-center">
                    <div className="col-span-3">
                      <Input
                        value={formula.cell}
                        onChange={(e) => {
                          const newFormulas = [...metadata.summary_formulas];
                          newFormulas[index].cell = e.target.value;
                          setMetadata(prev => ({ ...prev, summary_formulas: newFormulas }));
                        }}
                        placeholder="H20"
                      />
                    </div>
                    <div className="col-span-8">
                      <Input
                        value={formula.formula}
                        onChange={(e) => {
                          const newFormulas = [...metadata.summary_formulas];
                          newFormulas[index].formula = e.target.value;
                          setMetadata(prev => ({ ...prev, summary_formulas: newFormulas }));
                        }}
                        placeholder="=SUM(H{start_row}:H{end_row})"
                      />
                    </div>
                    <div className="col-span-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        onClick={() => {
                          const newFormulas = metadata.summary_formulas.filter((_, i) => i !== index);
                          setMetadata(prev => ({ ...prev, summary_formulas: newFormulas }));
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addSummaryFormula}
                  className="mt-2"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Summary Formula
                </Button>
              </CollapsibleContent>
            </Collapsible>

            <Collapsible className="mt-4">
              <CollapsibleTrigger className="flex items-center gap-2 w-full py-2 text-left font-medium">
                <ChevronDown className="h-4 w-4" />
                Merged Cells (Optional)
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Define merged cell ranges to handle them correctly during export.
                </p>
                <Alert className="bg-primary/10 border-primary/20">
                  <AlertDescription>
                    If your template has merged cells (like I8:J8), define them here to ensure data is written to the correct position.
                  </AlertDescription>
                </Alert>
              </CollapsibleContent>
            </Collapsible>
          </div>
        );

      case 4:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-4">Preview & Validation</h3>

            {validation.warnings.length > 0 && (
              <Alert variant="destructive" className="mb-4 bg-warning/10 border-warning/20">
                <AlertDescription className="text-warning">
                  <p className="font-medium mb-2">Validation Warnings:</p>
                  <ul className="list-disc list-inside text-sm">
                    {validation.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {validation.valid && (
              <Alert className="mb-4 border-success/20 bg-success/10">
                <AlertDescription className="text-success">
                  Metadata configuration is valid!
                </AlertDescription>
              </Alert>
            )}

            <Card className="mb-4">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Configuration Summary</CardTitle>
                  <Badge>
                    {safeMetadata.header_mappings.length} headers, {safeMetadata.table_config.columns.length} columns
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm mb-2">
                  <strong>Entity Type:</strong> {metadata.entity_type}
                </p>
                <p className="text-sm mb-4">
                  <strong>Table Configuration:</strong> Header row {safeMetadata.table_config.header_row},
                  Data starts at row {safeMetadata.table_config.start_row}
                </p>

                {safeMetadata.header_mappings.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium mb-2">Header Mappings:</p>
                    <div className="flex flex-wrap gap-1">
                      {metadata.header_mappings.map((mapping, index) => (
                        <Badge key={index} variant="outline">
                          {mapping.cell} → {mapping.mongodb_path}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {metadata.table_config.columns.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Column Mappings:</p>
                    <div className="flex flex-wrap gap-1">
                      {safeMetadata.table_config.columns.map((column, index) => (
                        <Badge
                          key={index}
                          variant={column.formula ? 'secondary' : 'outline'}
                        >
                          {column.column} → {column.mongodb_path || column.formula}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <p className="text-sm font-medium mb-2">Sample Mapping Preview</p>
            <p className="text-sm text-muted-foreground mb-4">
              Based on your configuration, here's how sample data would be mapped:
            </p>

            <Card>
              <CardContent className="p-4">
                <pre className="text-xs overflow-auto max-h-48 bg-muted p-2 rounded">
                  {JSON.stringify(generateSampleData(), null, 2)}
                </pre>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manual Template Mapper - {template?.name}</DialogTitle>
          <DialogDescription>
            Configure precise MongoDB field to Excel column mappings
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-1 py-4">
          {steps.map((label, index) => (
            <React.Fragment key={label}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    index <= activeStep
                      ? 'bg-primary/100 text-white'
                      : 'bg-muted/80 text-muted-foreground'
                  }`}
                >
                  {index + 1}
                </div>
                <span className="text-xs mt-1 text-muted-foreground hidden md:block">{label}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`w-8 h-0.5 ${index < activeStep ? 'bg-primary/100' : 'bg-muted/80'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="py-4">
          {renderStepContent()}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant="outline"
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Back
          </Button>
          {activeStep < steps.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={!validation.valid && activeStep > 0}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSave}
              disabled={!validation.valid}
            >
              <Save className="h-4 w-4 mr-2" />
              Save Configuration
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManualTemplateMapper;
